import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { PaginatedEntityApiService } from "../../../../shared/store/pagination/paginated-entity-api.service";
import { environment } from "../../../../../environments/environment";
import { map } from "rxjs/operators";
import { PiedDeChaineBuilder } from "../../../pied-de-chaine/models/pied-de-chaine-builder.model";
import { QueryParam } from "../../../../shared/store/store-types.model";
import { queryParamToHttp } from "../../../../shared/store/utils/store-utils.service";
import { Facture, FactureKpis, FactureLigne } from "../../models/facture.model";
import { BonLivraison } from "../../../bon-livraison/models/bon-livraison.model";
import { FactureMailPayload } from "../../models/facture-mail-payload.model";

@Injectable({ providedIn: "root" })
export class FactureApiService implements PaginatedEntityApiService<Facture> {
  httpClient = inject(HttpClient);

  loadOne(id: string): Observable<Facture> {
    return this.httpClient.get<Facture>(
      `${environment.api()}/factures/id/eager/${id}`,
    );
  }

  loadAll(query?: QueryParam): Observable<Facture[]> {
    let params = queryParamToHttp(query);
    return this.httpClient.get<Facture[]>(
      `${environment.api()}/factures/eager`,
      {
        params,
      },
    );
  }

  loadPage(
    page: number,
    limit: number,
    query?: QueryParam,
  ): Observable<{ data: Facture[]; total: number }> {
    const params = queryParamToHttp({ ...query, page, pageSize: limit });
    return this.httpClient.get<{ data: Facture[]; total: number }>(
      `${environment.api()}/factures/search`,
      { params },
    );
  }

  loadKpis(): Observable<FactureKpis> {
    return this.httpClient.get<FactureKpis>(
      `${environment.api()}/factures/kpis`,
    );
  }

  create(entity: Facture): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures`,
      entity,
    );
  }

  remove(id: string): Observable<string> {
    return this.httpClient
      .delete<PiedDeChaineBuilder>(`${environment.api()}/factures/id/${id}`)
      .pipe(map((o) => id));
  }

  update(entity: Facture): Observable<Facture> {
    return this.httpClient.put<Facture>(
      `${environment.api()}/factures/id/${entity.id}`,
      entity,
    );
  }

  saveLigne(entity: FactureLigne): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/ligne`,
      entity,
    );
  }

  deleteLigne(id: string): Observable<Facture> {
    return this.httpClient.delete<Facture>(
      `${environment.api()}/factures/ligne/id/${id}`,
    );
  }

  addBonLivraisonToFacture(
    bls: BonLivraison[],
    factureId: string,
  ): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/bl/factureId/${factureId}`,
      { bls },
    );
  }

  createFromBL(bl: BonLivraison): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/bl/create`,
      bl,
    );
  }

  removeBonLivraisonFromFacture(
    id: string,
    factureId: string,
  ): Observable<Facture> {
    return this.httpClient.delete<Facture>(
      `${environment.api()}/factures/bl/factureId/${factureId}/id/${id}`,
    );
  }

  onFiger(factureId: string): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/figer/${factureId}`,
      null,
    );
  }

  removeFactureFromAvoir(
    avoir: Facture,
    facture: Facture,
  ): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/remove_from_avoir/${avoir.id}/${facture.id}`,
      null,
    );
  }

  makeAvoirFromFacture(facture: Facture): Observable<Facture> {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/make_avoir`,
      facture,
    );
  }

  sendMail(payload: FactureMailPayload) {
    return this.httpClient.post<Facture>(
      `${environment.api()}/factures/send_mail`,
      payload,
    );
  }
}
